import {
  Box,
  Button as MuiButton,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  IconButton as MuiIconButton,
  Input,
  LinearProgress as MuiLinearProgress,
  MenuItem,
  Select as MuiSelect,
  Typography,
  createTheme,
  makeStyles
} from '@material-ui/core'
import { createGenerateClassName, StylesProvider, ThemeProvider } from '@material-ui/core/styles'
import React from 'react'
import { appHeaderHeight, colors, fonts, paddingButton, rounded, shadows, spacing, type } from './style-guide'

export { colors, rounded, spacing } from './style-guide'

const theme = createTheme({
  palette: {
    primary: {
      main: colors.brand100,
      dark: colors.brand75
    }
  },
  typography: {
    ...fonts.roboto,
    button: {
      textTransform: 'initial',
      fontSize: 14
    },
    body1: {
      fontSize: 14
    },
    allVariants: {
      color: colors.neutralDark800
    },
    htmlFontSize: 14,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    fontSize: 14,
    fontFamily: 'Roboto'
  },
  props: {
    MuiButtonBase: {
      disableRipple: true
    }
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: 4,
        boxShadow: 'none',
        lineHeight: 1
      },
      outlined: {
        padding: paddingButton.medium,
        border: `2px solid ${colors.brand100}`,
        color: colors.brand100,
        backgroundColor: colors.neutralLight0,
        '&:hover, &.hover, &:focus, &.focus': {
          backgroundColor: colors.brand10
        },
        '&:disabled': {
          color: colors.neutralLight40,
          border: `2px solid ${colors.neutralLight30}`
        }
      },
      contained: {
        boxShadow: 'none',
        padding: paddingButton.mediumPlus,
        color: colors.neutralLight0,
        backgroundColor: colors.brand100,
        '&:hover, &.hover, &:focus, &:active': {
          backgroundColor: colors.brand300
        },
        '&:disabled': {
          backgroundColor: colors.neutralLight20,
          color: colors.neutralLight50
        }
      },
      text: {
        padding: paddingButton.mediumPlus,
        backgroundColor: 'transparent',
        color: colors.brand100,
        '&:hover, &.hover, &:focus, &:active': {
          backgroundColor: colors.brand10
        },
        '&:disabled': {
          backgroundColor: colors.neutralLight0,
          color: colors.neutralLight40
        }
      }
    }
  }
})

function setVhCustomProperty() {
  if (document.documentElement.style.getPropertyValue('--vh') !== '') {
    return
  }

  const computeVh = () => {
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
  }

  computeVh()
  window.addEventListener('resize', computeVh)
}

interface AppThemeProps {
  seed?: string
  children?: React.ReactNode
}

export const AppTheme: React.FC<AppThemeProps> = ({ children, seed = 'portal' }) => {
  React.useEffect(() => {
    setVhCustomProperty()
  }, [])

  const generateClassName = createGenerateClassName({
    productionPrefix: 'c',
    seed
  })

  return (
    <StylesProvider generateClassName={generateClassName}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </StylesProvider>
  )
}

export const AppLayout: React.FC<{ children?: React.ReactNode }> & {
  Header: React.FC<{ children?: React.ReactNode }>
  Body: React.FC<{ children?: React.ReactNode }>
} = ({ children }) => {
  const header = React.Children.toArray(children).find(
    child => React.isValidElement(child) && child.type === AppLayout.Header
  ) as React.ReactElement | undefined
  const body = React.Children.toArray(children).find(
    child => React.isValidElement(child) && child.type === AppLayout.Body
  ) as React.ReactElement | undefined

  return (
    <div style={{ height: '100%', display: 'grid', gridTemplateRows: 'auto 1fr' }}>
      {header && <div>{header.props.children}</div>}
      {body && <main style={{ height: '100%', maxHeight: '100%', position: 'relative' }}>{body.props.children}</main>}
    </div>
  )
}

AppLayout.Header = () => null
AppLayout.Body = () => null

export const ScrollContainer: React.FC<{
  className?: string
  style?: React.CSSProperties
  dataCy?: string
  children?: React.ReactNode
}> = ({ children, className = '', style = {}, dataCy }) => (
  <div
    className={`scroll-container ${className}`}
    style={{ position: 'relative', height: '100%', overflowY: 'auto', ...style }}
    data-cy={dataCy}
  >
    {children}
  </div>
)

const useFlexStyles = makeStyles({
  base: { width: '100%', height: '100%' }
})

export const Flex: React.FC<{
  direction?: 'row' | 'column'
  justify?: 'center' | 'end' | 'start' | 'space-around' | 'space-between'
  align?: 'baseline' | 'start' | 'center' | 'end' | 'stretch'
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode
}> = ({ children, direction = 'row', justify = 'start', align = 'start', className = '', style = {} }) => {
  const classes = useFlexStyles()
  const justifyMap = {
    center: 'center',
    end: 'flex-end',
    start: 'flex-start',
    'space-around': 'space-around',
    'space-between': 'space-between'
  }
  const alignMap = {
    baseline: 'baseline',
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    stretch: 'stretch'
  }

  return (
    <div
      className={`${classes.base} ${className}`}
      style={{
        display: 'flex',
        flexDirection: direction,
        justifyContent: justifyMap[justify],
        alignItems: alignMap[align],
        ...style
      }}
    >
      {children}
    </div>
  )
}

const useAppHeaderStyles = makeStyles({
  container: {
    position: 'relative',
    height: appHeaderHeight,
    ...spacing.paddingX.s,
    display: 'flex',
    alignItems: 'center'
  },
  appHeaderWithBorder: {
    borderBottom: `1px solid ${colors.neutralLight50}`
  }
})

export const useHeaderStyles = () => {
  const classes = useAppHeaderStyles()
  return {
    appHeader: classes.container,
    appHeaderBoxShadow: `${classes.container}`,
    appHeaderWithBorder: classes.appHeaderWithBorder
  }
}

export const AppHeader: React.FC<{
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
}> = ({ children, className = '', style = {} }) => {
  const classes = useAppHeaderStyles()
  return (
    <header className={`${classes.container} ${className}`} style={style}>
      {children}
    </header>
  )
}

export enum LogoSizes {
  small = 'small',
  medium = 'medium'
}

const useLogoStyles = makeStyles({
  small: {
    width: '1.5rem',
    height: '1.5rem'
  },
  medium: {
    width: spacing.m,
    height: spacing.m
  },
  mark: {
    alignItems: 'center',
    backgroundColor: colors.brand100,
    borderRadius: '50%',
    color: colors.neutralLight0,
    display: 'inline-flex',
    fontWeight: 700,
    justifyContent: 'center'
  }
})

export const Logo: React.FC<{ size?: LogoSizes }> = ({ size = LogoSizes.small }) => {
  const classes = useLogoStyles()
  return <span className={`${classes.mark} ${classes[size]}`}>A</span>
}

export const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof MuiButton> & { appearance?: 'danger' | 'warning' }
>(function Button({ appearance, style, ...props }, ref) {
  const appearanceStyle =
    appearance === 'danger'
      ? { color: colors.neutralLight0, backgroundColor: colors.signalError100 }
      : appearance === 'warning'
        ? { color: colors.neutralLight0, backgroundColor: colors.signalWarning100 }
        : {}

  return (
    <MuiButton ref={ref} variant="contained" disableElevation style={{ ...appearanceStyle, ...style }} {...props} />
  )
})

const useIconButtonStyles = makeStyles({
  root: {
    padding: spacing.xxs,
    ...rounded.m,
    color: colors.neutralDark500,
    '&:hover, &:active, &:focus': {
      backgroundColor: colors.brand10,
      color: colors.neutralDark800
    }
  }
})

export const IconButton = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof MuiIconButton>>(
  function IconButton({ children, ...props }, ref) {
    const classes = useIconButtonStyles()
    return (
      <MuiIconButton ref={ref} classes={{ root: classes.root }} {...props}>
        <span style={{ display: 'inline-flex', width: spacing.s, height: spacing.s }}>{children}</span>
      </MuiIconButton>
    )
  }
)

export const Spinner: React.FC<{
  size?: 'xs' | 's' | 'm'
  color?: string
  padding?: string
}> = ({ size = 'm', color = colors.brand100, padding }) => {
  const internalSize = size === 'xs' ? 7 : size === 's' ? 20 : 40
  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        flex: 1,
        justifyContent: 'center',
        padding: padding ?? (size === 'xs' ? 0 : spacing.xs),
        width: '100%'
      }}
    >
      <CircularProgress color="inherit" size={internalSize} style={{ color }} />
    </div>
  )
}

const svgProps = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24'
}

export const Collapse = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...svgProps} {...props}>
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M9.707 7.293a1 1 0 01.083 1.32l-.083.094-2.586 2.586a1 1 0 00-.083 1.32l.083.094 2.586 2.586a1 1 0 01-1.32 1.497l-.094-.083-2.586-2.586a2.999 2.999 0 01-.135-4.098l.135-.144 2.586-2.586a1 1 0 011.414 0zm8 0a1 1 0 01.083 1.32l-.083.094-2.586 2.586a1 1 0 00-.083 1.32l.083.094 2.586 2.586a1 1 0 01-1.32 1.497l-.094-.083-2.586-2.586a2.999 2.999 0 01-.135-4.098l.135-.144 2.586-2.586a1 1 0 011.414 0z"
    />
  </svg>
)

export const Expand = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...svgProps} {...props}>
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M13.121 7.293a1 1 0 00-.083 1.32l.083.094 2.586 2.586a1 1 0 01.083 1.32l-.083.094-2.586 2.586a1 1 0 001.32 1.497l.095-.083 2.585-2.586a2.999 2.999 0 00.135-4.098l-.135-.144-2.585-2.586a1.002 1.002 0 00-1.415 0zm-8 0a1 1 0 00-.083 1.32l.083.094 2.586 2.586a1 1 0 01.083 1.32l-.083.094-2.586 2.586a1 1 0 001.32 1.497l.095-.083 2.585-2.586a2.999 2.999 0 00.135-4.098l-.135-.144-2.585-2.586a1.002 1.002 0 00-1.415 0z"
    />
  </svg>
)

export const Cross = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...svgProps} {...props}>
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M17.657 6.343c.362.363.388.935.078 1.327l-.078.087L13.414 12l4.243 4.243a1 1 0 01-1.327 1.492l-.087-.078L12 13.414l-4.243 4.243a1 1 0 01-1.492-1.327l.078-.087L10.586 12 6.343 7.757A1 1 0 017.67 6.265l.087.078L12 10.586l4.243-4.243a1 1 0 011.414 0z"
    />
  </svg>
)

export const Warning = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...svgProps} fill="none" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.865 2.513a1 1 0 00-1.743 0L2.124 18.51A1 1 0 002.995 20h17.997a1 1 0 00.872-1.49L12.865 2.513zM11 8a1 1 0 112 0v5a1 1 0 11-2 0V8zm0 9a1 1 0 112 0 1 1 0 01-2 0z"
      fill="currentColor"
    />
  </svg>
)

export enum IconNames {
  calendar = 'calendar',
  user = 'user'
}

export const Icon: React.FC<{ name: IconNames; size?: 's' | 'm' }> = ({ name, size = 's' }) => {
  const iconSize = size === 'm' ? spacing.m : spacing.s
  const common = {
    width: iconSize,
    height: iconSize,
    color: colors.neutralDark800,
    style: { marginRight: spacing.xxs }
  }

  if (name === IconNames.user) {
    return (
      <svg {...svgProps} {...common}>
        <path
          fill="currentColor"
          d="M12 12a4 4 0 100-8 4 4 0 000 8zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z"
        />
      </svg>
    )
  }

  return (
    <svg {...svgProps} {...common}>
      <path
        fill="currentColor"
        d="M7 2a1 1 0 011 1v1h8V3a1 1 0 112 0v1h1a2 2 0 012 2v13a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2h1V3a1 1 0 011-1zm12 8H5v9h14v-9z"
      />
    </svg>
  )
}

interface OptionProps {
  value?: string
  label?: string
  children?: React.ReactNode
}

export const Option: React.FC<OptionProps> = () => null

export const Select: React.FC<{
  label: string
  value?: string | Array<string>
  onChange?: (value: any) => void
  multiple?: boolean
  children?: React.ReactNode
}> = ({ label, value, onChange, multiple = false, children }) => {
  const options = React.Children.toArray(children)
    .filter(React.isValidElement)
    .map(child => child as React.ReactElement<OptionProps>)

  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', marginBottom: spacing.xxs }}>{label}</span>
      <MuiSelect
        fullWidth
        multiple={multiple}
        value={value ?? (multiple ? [] : '')}
        onChange={event => onChange?.(event.target.value)}
        variant="outlined"
      >
        {options.map(option => (
          <MenuItem key={option.props.value} value={option.props.value}>
            {option.props.children ?? option.props.label}
          </MenuItem>
        ))}
      </MuiSelect>
    </label>
  )
}

export const InputField: React.FC<{
  label?: string
  value: string
  onChange: (value: string) => void
  className?: string
  compact?: boolean
}> = ({ label, value, onChange, className, compact }) => (
  <label className={className} style={{ display: 'block' }}>
    {label && <span style={{ display: 'block', marginBottom: spacing.xxs }}>{label}</span>}
    <Input
      fullWidth
      disableUnderline
      value={value}
      onChange={event => onChange(event.target.value)}
      style={{
        backgroundColor: colors.neutralLight20,
        border: `1px solid ${colors.neutralLight50}`,
        borderRadius: rounded.m.borderRadius,
        padding: compact ? spacing.xxs : spacing.xs
      }}
    />
  </label>
)

export const List: React.FC<{ divided?: boolean; children?: React.ReactNode }> = ({ divided, children }) => (
  <ul
    style={{
      borderTop: divided ? `1px solid ${colors.neutralLight40}` : undefined,
      listStyle: 'none',
      margin: 0,
      padding: 0
    }}
  >
    {children}
  </ul>
)

export const ListItem: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <li style={{ borderBottom: `1px solid ${colors.neutralLight40}` }}>{children}</li>
)

export const Panel: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <aside style={{ height: '100%', pointerEvents: 'none', position: 'relative' }}>
    <div
      style={{
        backgroundColor: colors.neutralLight0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        pointerEvents: 'initial'
      }}
    >
      {children}
    </div>
  </aside>
)

export const PanelSection: React.FC<{
  paddingTop?: boolean
  paddingBottom?: boolean
  children?: React.ReactNode
}> = ({ paddingTop, paddingBottom, children }) => (
  <section
    style={{
      paddingLeft: spacing.xs,
      paddingRight: spacing.xs,
      paddingTop: paddingTop ? spacing.s : undefined,
      paddingBottom: paddingBottom ? spacing.s : undefined
    }}
  >
    <div style={spacing.padding.xxs}>{children}</div>
  </section>
)

export const PanelSectionHeader: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <header style={{ alignItems: 'center', display: 'flex', marginBottom: spacing.xs }}>{children}</header>
)

export const LinearProgress: React.FC<{ value: number }> = ({ value }) => (
  <Box display="flex" alignItems="center">
    <Box width="100%" mr={1}>
      <MuiLinearProgress variant="determinate" value={value} />
    </Box>
    <Box minWidth={35}>
      <Typography variant="body2" color="textSecondary">
        {`${Math.round(value)}%`}
      </Typography>
    </Box>
  </Box>
)

export const TipBar: React.FC<{ tipContent: string }> = ({ tipContent }) => (
  <div
    style={{
      backgroundColor: colors.neutralLight20,
      padding: `${spacing.xxs} ${spacing.s}`
    }}
  >
    <span>{tipContent}</span>
  </div>
)

export const NotificationMessage: React.FC<{
  icon: JSX.Element
  message: string
  onClose?: () => void
  children?: React.ReactNode
}> = ({ icon, message, children }) => (
  <Card style={{ pointerEvents: 'all', width: 400 }}>
    <div style={{ alignItems: 'center', display: 'flex', padding: spacing.xs }}>
      <span
        style={{
          alignItems: 'center',
          backgroundColor: colors.signalWarning100,
          borderRadius: spacing.xxxs,
          color: colors.neutralLight0,
          display: 'inline-flex',
          height: spacing.s,
          justifyContent: 'center',
          minWidth: spacing.s,
          width: spacing.s
        }}
      >
        {icon}
      </span>
      <div style={{ flexGrow: 1, marginLeft: spacing.xxs }}>{message}</div>
    </div>
    {children && <div style={{ padding: `0 ${spacing.xs} ${spacing.xs} ${spacing.xxl}` }}>{children}</div>}
  </Card>
)

export const NotificationMessageContent: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => <div>{children}</div>

export const NotificationMessageContentActions: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => <div style={{ marginTop: spacing.xxs }}>{children}</div>

export const GridLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ margin: spacing.l }}>
    <Grid container spacing={3}>
      {React.Children.map(children, child => (
        <Grid item xs={4}>
          {child}
        </Grid>
      ))}
    </Grid>
  </div>
)

export const ImageCard: React.FC<{
  imageSrc: string
  children?: React.ReactNode
}> = ({ imageSrc, children }) => (
  <Card>
    <CardActionArea>
      <CardMedia style={{ height: '10rem' }} image={imageSrc} />
      <CardContent style={{ fontSize: type.m.fontSize }}>{children}</CardContent>
    </CardActionArea>
  </Card>
)
